import React from "react";
import { Filter, Inbox, Plus, RotateCcw, SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { createEntityActionLabel, singularizeEntityName } from "@/lib/utils/crm/entity-label";

export interface EmptyStateProps {
  entityName: string;
  /**
   * True when a "Created By" filter is applied. An empty result then has an
   * innocent second explanation — the column is only populated from
   * 2026-07-06 and never for automated/imported records — so we say so rather
   * than letting the blank table imply the person created nothing.
   */
  createdByFilterActive?: boolean;
  /** A search term is narrowing the result set. */
  searchTerm?: string;
  /** Some filter (quick, column, or advanced) is narrowing the result set. */
  hasActiveFilters?: boolean;
  /** Clear search + every filter. Renders the primary recovery action. */
  onClearFilters?: () => void;
  /** Open the create flow for this entity. */
  onCreate?: () => void;
}

/**
 * Empty state for the CRM table.
 *
 * Two distinct situations used to render the same dead end — "No X found. Try
 * adjusting your search or filters." — with no way to act on it:
 *
 *   1. Filters/search exclude everything → the fix is to CLEAR them, so offer
 *      that button rather than describing it (ERROR_STATES_ACTIONABLE).
 *   2. The entity genuinely has no records → telling the user to adjust filters
 *      is wrong advice; the fix is to create the first record.
 */
export const EmptyState: React.FC<EmptyStateProps> = React.memo(
  ({
    entityName,
    createdByFilterActive = false,
    searchTerm,
    hasActiveFilters = false,
    onClearFilters,
    onCreate,
  }) => {
    const trimmedSearch = searchTerm?.trim() ?? "";
    const isNarrowed = Boolean(trimmedSearch) || hasActiveFilters || createdByFilterActive;
    const singular = singularizeEntityName(entityName).toLowerCase();

    const Icon = isNarrowed ? (trimmedSearch ? SearchX : Filter) : Inbox;

    return (
      <div
        className="text-center py-16 text-muted-foreground bg-background"
        data-testid="crm-table-empty-state"
      >
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
            <Icon className="h-6 w-6 text-muted-foreground" aria-hidden />
          </div>

          <p className="text-lg font-medium text-foreground">
            {isNarrowed
              ? `No ${entityName.toLowerCase()} match this view`
              : `No ${entityName.toLowerCase()} yet`}
          </p>

          {createdByFilterActive ? (
            <p className="text-sm max-w-md">
              &ldquo;Created By&rdquo; is only recorded for records created after
              creator tracking was added, and not for records created by an
              import, automation, or sync. An empty result here does not mean
              this person created no {entityName.toLowerCase()}.
            </p>
          ) : isNarrowed ? (
            <p className="text-sm max-w-md">
              {trimmedSearch ? (
                <>
                  Nothing matches <span className="font-medium">“{trimmedSearch}”</span>
                  {hasActiveFilters ? " with the current filters." : "."}
                </>
              ) : (
                "The current filters exclude every record."
              )}
            </p>
          ) : (
            <p className="text-sm max-w-md">
              Once you add a {singular}, it will show up here.
            </p>
          )}

          {/* Offer the fix in place instead of describing it. */}
          {(onClearFilters || onCreate) && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              {isNarrowed && onClearFilters && (
                <Button variant="outline" size="sm" onClick={onClearFilters}>
                  <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden />
                  Clear search and filters
                </Button>
              )}
              {onCreate && (
                <Button
                  size="sm"
                  variant={isNarrowed ? "ghost" : "default"}
                  onClick={onCreate}
                >
                  <Plus className="mr-1.5 h-4 w-4" aria-hidden />
                  {createEntityActionLabel(entityName)}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

EmptyState.displayName = "EmptyState";
